import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Profile } from "./types";
import { useNotifications } from "@/hooks/useNotifications";
import { useAuth } from "@/contexts/AuthContext";

export const useProfileQuery = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async (): Promise<Profile> => {
      console.log("Iniciando consulta de perfil...");
      
      try {
        if (!user) {
          console.error("No se encontró usuario autenticado");
          throw new Error("No authenticated user");
        }

        console.log("Usuario encontrado:", user.id);

        // Get profile data
        const { data: profile, error: profileError } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", user.id)
          .single();

        if (profileError) {
          console.error("Error obteniendo perfil:", profileError);
          throw profileError;
        }

        // Get role from user_roles table (more secure than profiles.role)
        const { data: userRoles, error: rolesError } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", user.id);

        if (rolesError) {
          console.error("Error obteniendo roles:", rolesError);
          // Fallback to profile role if user_roles fails
        }

        // Determine the effective role (priority: user_roles > profiles.role)
        let effectiveRole: string = profile?.role || 'socio';
        if (userRoles && userRoles.length > 0) {
          // Priority order: admin > manager > profesor > monitor > socio
          const rolePriority: string[] = ['admin', 'manager', 'profesor', 'monitor', 'socio'];
          const userRoleValues: string[] = userRoles.map(r => r.role as string);
          for (const role of rolePriority) {
            if (userRoleValues.includes(role)) {
              effectiveRole = role;
              break;
            }
          }
        }

        if (!profile) {
          console.error("No se encontró perfil para el usuario:", user.id);
          return {
            id: user.id,
            username: null,
            avatar_url: null,
            role: effectiveRole,
            first_name: null,
            last_name: null,
            banned: false,
            gym_id: null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            birthdate: null,
            dni: null,
            phone: null,
            address: null,
            emergency_phone: null,
            health_insurance: null
          };
        }

        console.log("Perfil obtenido:", {
          ...profile,
          first_name: profile.first_name,
          last_name: profile.last_name,
          birthdate: profile.birthdate,
          effectiveRole
        });
        
        return { ...profile, role: effectiveRole } as Profile;
      } catch (error: any) {
        console.error("Error en la consulta del perfil:", error);
        
        // Solo mostrar notificación de error si NO es por falta de autenticación
        // y si no estamos en rutas de autenticación
        const currentPath = window.location.pathname;
        const isAuthRoute = ['/auth', '/intro'].includes(currentPath);
        
        if (!error.message?.includes('not authenticated') && !isAuthRoute) {
          const { error: showError } = useNotifications();
          showError("No se pudo cargar el perfil.");
        }
        
        throw error;
      }
    },
    enabled: !!user, // Solo ejecutar la query si el usuario está autenticado
    retry: (failureCount, error) => {
      // No reintentar si es un error de autenticación
      if (error?.message?.includes('not authenticated')) {
        return false;
      }
      return failureCount < 2;
    },
    staleTime: 30000, // 30 segundos - reducido para actualizar más frecuentemente
    refetchOnWindowFocus: true, // Habilitar para refrescar cuando la ventana vuelve a tener foco
  });
};
